import React from "react";
import {Link, useLocation} from "react-router-dom";
//local
import {
  ORGANIZATIONS,
  INVITE_COMPANIES,
  OUR_FAVOURITES_SUPPLIERS,
  FRAMEWORK_AGREEMENTS,
  OUR_BLACKLIST,
  PROJECTS,
  CONTRACTS,
  REQUESTS_TO_US,
  CONTRACTS_TO_US,
  PUBLIC_RFTS,
  OUR_OFFERS,
  JOINING_ORG,
  OUR_QUALIFICATIONS,
  SIGN_CONTRACTS,
  toOrganizationId,
} from "../constants/routes.const";
import {useTranslation} from "react-i18next";
import {CustomBadge} from "./CustomBadge";
import {
  IconTheater,
  IconSearch,
  IconPlane,
  IconContract,
  IconSkullEmpty,
  IconCheckMark,
  IconBriefcase,
  IconInbox,
  IconOutbox,
  IconRamavtal,
  IconBinoculars,
  IconWallet,
  IconFeather,
  IconAi,
} from "components/icons";
import ButtonCloseDrawer from "./buttons/ButtonCloseDrawer";
import {hasPermission, hasPermissions, openNewTab} from "helpers/helper";
import {LockValue} from "components/LockValue";
import {Tag} from "components/Tag";
import {color} from "constants/color.consts";
import {hashes} from "constants/tabs.const";
import cn from "classnames";

export const SideBar = ({auth, organization, seller, clientStore, showSidebar}) => {
  const {t} = useTranslation();
  const {activeOrganizationId, token, user} = auth;
  const {unreadRequests, unsignedSellerContracts} = seller;
  const location = useLocation();
  const isDisabled = !token || location.pathname.includes(JOINING_ORG);

  const isShowProjectsContracts = hasPermissions([
    "READ_PROJECTS",
    "READ_BUYER_CONTRACTS",
  ]);
  const isShowOurSuppliers = hasPermission(["READ_OUR_SUPPLIERS"]);

  const isActiveLink = (link) => location.pathname.includes(link);
  const handleClick = (e, permissions) =>
    token
      ? !!permissions
        ? hasPermission(permissions ?? [])
          ? clientStore.setShowSidebar(false)
          : e.preventDefault()
        : clientStore.setShowSidebar(false)
      : e.preventDefault();

  const toHandyAIPage = async (e, permissions) => {
    e.preventDefault();
    clientStore.setShowSidebar(false);

    if (!hasPermission(permissions)) return openNewTab(t("HANDY_AI_SALES_PAGE_LINK"));

    const newTab = window.open("", "_blank");
    if (!newTab) return console.error(t("HANDY_AI_NEW_TAB_FAILED"));

    try {
      const data = await auth.getHandyAIToken();
      const params = `token=${data.token}&returnUrl=${window.location.origin}`;
      newTab.location.href = `${t("HANDY_AI_APPLICATION_LINK")}?${params}`;
    } catch (error) {
      newTab.close();
      console.error(t("HANDY_AI_GET_TOKEN_FAILED"), error);
    }
  };

  return (
    <div className={`main-sidebar_wrapper ${showSidebar}`}>
      <ButtonCloseDrawer
        closeDrawer={() => clientStore.setShowSidebar(false)}
        addClass="main-sidebar_btn__close"
        ifOpen={showSidebar === "showSidebar"}
      />
      <div className="main-sidebar_list">
        <h5
          className={cn("main-sidebar_section blue __uppercase", {disabled: isDisabled})}
        >
          {t("PROCUREMENT_SECTION")}
        </h5>
        <LockValue
          canPermissions={["READ_PROJECTS", "READ_BUYER_CONTRACTS"]}
          needAllPermissions
          fullSubcribeButton
          fullSubscribeVerticalPosition
          fullSubcribeButtonClassName="main-sidebar_section-locked"
          addClassDescription={isDisabled ? "light-gray" : ""}
        >
          <Link
            to={PROJECTS}
            end="true"
            disabled={isDisabled}
            className={cn("main-sidebar_btn", {active: isActiveLink(PROJECTS)})}
            onClick={(e) => handleClick(e, ["READ_PROJECTS"])}
          >
            <IconBriefcase />
            {t("PROJECTS_LINK")}
          </Link>
          <Link
            to={CONTRACTS}
            end="true"
            disabled={isDisabled}
            className={cn("main-sidebar_btn", {
              active: isActiveLink(CONTRACTS) && !isActiveLink(CONTRACTS_TO_US),
            })}
            onClick={(e) => handleClick(e, ["READ_BUYER_CONTRACTS"])}
          >
            <IconContract />
            {t("CONTRACTS_LINK")}
          </Link>
        </LockValue>

        <LockValue
          canPermissions={["READ_OUR_SUPPLIERS"]}
          justHide={!isShowProjectsContracts}
          fullSubcribeButton={isShowProjectsContracts}
          fullSubscribeVerticalPosition
          fullSubcribeButtonClassName="main-sidebar_section-locked"
        >
          <Link
            to={OUR_FAVOURITES_SUPPLIERS}
            end="true"
            disabled={isDisabled}
            className={cn("main-sidebar_btn", {
              active: isActiveLink(OUR_FAVOURITES_SUPPLIERS),
            })}
            onClick={(e) => handleClick(e, ["READ_OUR_SUPPLIERS"])}
          >
            <IconCheckMark />
            {t("OUR_FAVOURITES_LINK")}
          </Link>
        </LockValue>

        <LockValue
          canPermissions={["READ_OUR_BLACKLIST", "READ_ORGANIZATION_RAMAVTAL"]}
          justHide={!(isShowOurSuppliers && isShowProjectsContracts)}
          needAllPermissions
          fullSubcribeButton={isShowOurSuppliers && isShowProjectsContracts}
          fullSubscribeVerticalPosition
          fullSubcribeButtonClassName="main-sidebar_section-locked"
        >
          {!!organization?.blacklistOrganization.length && (
            <Link
              to={OUR_BLACKLIST}
              end="true"
              disabled={isDisabled}
              className={cn("main-sidebar_btn", {
                active: isActiveLink(OUR_BLACKLIST),
              })}
              onClick={(e) => handleClick(e, ["READ_OUR_BLACKLIST"])}
            >
              <IconSkullEmpty />
              {t("OUR_BLACKLIST_LINK")}
            </Link>
          )}
          <Link
            to={FRAMEWORK_AGREEMENTS}
            end="true"
            disabled={isDisabled}
            className={cn("main-sidebar_btn __wrap", {
              active: isActiveLink(FRAMEWORK_AGREEMENTS),
            })}
            onClick={(e) => handleClick(e, ["READ_ORGANIZATION_RAMAVTAL"])}
          >
            <IconRamavtal />
            {t("RAMAVTAL_LINK")}
          </Link>
        </LockValue>
        <h5
          className={cn("main-sidebar_section green __uppercase", {disabled: isDisabled})}
        >
          {t("TENDER_SECTION")}
        </h5>
        <LockValue
          canPermissions={["READ_PUBLIC_RFTS"]}
          needLabel
          needShowModalToSubscribe={!isDisabled}
          dimmedWhenLocked
          noLockIcon
        >
          <Link
            to={PUBLIC_RFTS}
            end="true"
            disabled={isDisabled}
            className={cn("main-sidebar_btn", {active: isActiveLink(PUBLIC_RFTS)})}
            onClick={(e) => handleClick(e, ["READ_PUBLIC_RFTS"])}
          >
            <IconBinoculars />
            {t("PUBLIC_RFP_LINK")}
            {!isActiveLink(PUBLIC_RFTS) && !isDisabled && (
              <span className="live">{t("MAIN_MENU_BADGE_LIVE")}</span>
            )}
          </Link>
        </LockValue>
        {user.hasSellerQualifications && (
          <LockValue
            canPermissions={["READ_QUALIFICATIONS"]}
            needLabel
            needShowModalToSubscribe={!isDisabled}
            dimmedWhenLocked
            noLockIcon
            badgeCount={user.unreadSellerQualificationsCount}
          >
            <Link
              to={OUR_QUALIFICATIONS}
              end="true"
              className={cn("main-sidebar_btn", {
                active: isActiveLink(OUR_QUALIFICATIONS),
              })}
              onClick={(e) => handleClick(e, ["READ_QUALIFICATIONS"])}
            >
              <IconCheckMark />
              {t("OUR_QUALIFICATIONS_LINK")}
              <CustomBadge count={user.unreadSellerQualificationsCount} />
            </Link>
          </LockValue>
        )}
        <LockValue
          canPermissions={["READ_SELLER_RFTS"]}
          needLabel
          needShowModalToSubscribe={!isDisabled}
          dimmedWhenLocked
          noLockIcon
        >
          <Link
            to={REQUESTS_TO_US}
            end="true"
            disabled={isDisabled}
            className={cn("main-sidebar_btn", {active: isActiveLink(REQUESTS_TO_US)})}
            onClick={(e) => handleClick(e, ["READ_SELLER_RFTS"])}
          >
            <IconInbox />
            {t("REQUESTS_TO_US_LINK")}
            <CustomBadge count={unreadRequests} />
          </Link>
        </LockValue>
        <LockValue
          canPermissions={["READ_OFFERS"]}
          needLabel
          needShowModalToSubscribe={!isDisabled}
          dimmedWhenLocked
          noLockIcon
        >
          <Link
            to={OUR_OFFERS}
            end="true"
            disabled={isDisabled}
            className={cn("main-sidebar_btn", {active: isActiveLink(OUR_OFFERS)})}
            onClick={(e) => handleClick(e, ["READ_OFFERS"])}
          >
            <IconOutbox />
            {t("OUR_OFFERS_LINK")}
          </Link>
        </LockValue>
        <LockValue
          canPermissions={["READ_SELLER_CONTRACTS"]}
          needLabel
          needShowModalToSubscribe={!isDisabled}
          badgeCount={unsignedSellerContracts}
          dimmedWhenLocked
          noLockIcon
        >
          <Link
            to={CONTRACTS_TO_US}
            end="true"
            disabled={isDisabled}
            className={cn("main-sidebar_btn", {
              active: isActiveLink(CONTRACTS_TO_US),
            })}
            onClick={(e) => handleClick(e, ["READ_SELLER_CONTRACTS"])}
          >
            <IconContract />
            {t("CONTRACTS_LINK")}
            <CustomBadge count={unsignedSellerContracts} />
          </Link>
        </LockValue>
        <h5
          className={cn("main-sidebar_section black __uppercase", {disabled: isDisabled})}
        >
          {t("BOTH_SECTION")}
        </h5>
        {/* 
        <Link
          to={FEEDS}
          end="true"
          className="main-sidebar_btn"
          onClick={handleClick}
        >
          <IconBullHorn />
          {t("SAY_SOMETHING")}
        </Link>
        */}
        <Link
          end="true"
          disabled={isDisabled}
          className={cn("main-sidebar_btn", {
            disabled: isDisabled,
          })}
          onClick={(e) => toHandyAIPage(e, ["READ_HANDY_AI_EXTENSION"])}
        >
          <IconAi />
          {t("HANDY_AI_LINK")}

          <Tag
            text="MAIN_MENU_BADGE_NEW"
            needTranslation
            color={color.red}
            uppercase
            tiny
            noBackground
            disabled={isDisabled}
            addClass="ml-2"
          />
        </Link>
        {/*
        {isShowProjectsContracts && !isShowOurSuppliers && (
          <img className="main-sidebar_sidebar-star-no-ai" src={SidebarStarAI} alt="" />
        )}
        */}
        <Link
          to={SIGN_CONTRACTS}
          end="true"
          disabled={isDisabled}
          className={cn("main-sidebar_btn mb-4", {
            active: isActiveLink(`${SIGN_CONTRACTS}`),
          })}
          onClick={handleClick}
        >
          <IconFeather color={color.black} />
          {t("SIGN_CONTRACTS_LINK")}
        </Link>
        <Link
          to={ORGANIZATIONS}
          end="true"
          disabled={isDisabled}
          className={cn("main-sidebar_btn", {
            active: location.pathname === ORGANIZATIONS,
          })}
          onClick={handleClick}
        >
          <IconSearch />
          {t("FIND_COMPANIES_LINK")}
        </Link>
        <LockValue
          canPermissions={["READ_INVITE_COMPANIES"]}
          needLabel
          needShowModalToSubscribe={!isDisabled}
          dimmedWhenLocked
          noLockIcon
        >
          <Link
            to={INVITE_COMPANIES}
            end="true"
            disabled={isDisabled}
            className={cn("main-sidebar_btn", {
              active: isActiveLink(INVITE_COMPANIES),
            })}
            onClick={(e) => handleClick(e, ["READ_INVITE_COMPANIES"])}
          >
            <IconPlane />
            {t("SIDE_INVITE_COMPANIES")}
          </Link>
        </LockValue>
        <Link
          to={isDisabled ? undefined : toOrganizationId(activeOrganizationId)}
          end="true"
          disabled={isDisabled}
          className={cn("main-sidebar_btn", {
            active: isActiveLink(`${ORGANIZATIONS}/${activeOrganizationId}`),
          })}
          onClick={handleClick}
        >
          <IconTheater />
          {t("OUR_PROFILE_LINK")}
        </Link>
        <Link
          to={isDisabled ? undefined : `${location.pathname}${hashes.SUBSCRIPTION}`}
          end="true"
          disabled={isDisabled}
          className="main-sidebar_btn"
        >
          <IconWallet />
          {t("OUR_SUBSCRIPTIONS_LINK")}
        </Link>
      </div>
    </div>
  );
};
